import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CreateSectionBodyDto, SectionDto, SectionObjectType } from '@picflow/types';
import { BehaviorSubject, Observable, Subject, filter, lastValueFrom, map } from 'rxjs';
import { assetSectionWidths, defaultAssetSection } from '../lib/default-asset-section';
import { objectsMatch } from '../lib/objects-match';
import { AppConfigService } from '../providers/config.service';
import { DataLoaderService } from './data-loader.service';
import { GalleryPasswordService } from './gallery-password.service';
import { TenantService } from './tenant.service';

@Injectable({
  providedIn: 'root',
})
export class SectionService {
  private _updates: Subject<[string, string, string?]> = new BehaviorSubject<[string, string, string?]>(undefined);
  public readonly onUpdates: Observable<[string, string, string?]> = this._updates.asObservable();
  public _moved: Subject<string> = new BehaviorSubject<string>(undefined);
  public readonly onMoved: Observable<string> = this._moved.asObservable();
  public sectionUpdateTimeMap: Map<string, number> = new Map();

  constructor(
    private http: HttpClient,
    private config: AppConfigService,
    private loader: DataLoaderService,
    private galleryPasswordService: GalleryPasswordService,
    private tenantService: TenantService
  ) {}

  public getSectionsSteam(galleryId: string, options: { ignore401Errors?: boolean } = {}) {
    let extra = `?gallery=${galleryId}${this.getPassword(galleryId)}`;
    if (options.ignore401Errors) extra += '&ignore_401_error=true';

    return this.http.get<{ data: SectionDto[] }>(`${this.config.apiEndpoint}/sections${extra}`).pipe(
      filter(Boolean),
      map(resp => {
        return resp.data
          .map(s => this.fixSection(s))
          .filter(s => s.position !== 612512)
          .sort((a, b) => (a.position > b.position ? 1 : -1));
      })
    );
  }

  public async getSections(galleryId: string, options: { ignore401Errors?: boolean } = {}): Promise<SectionDto[]> {
    return this.loader.get(`sections_${galleryId}`, () => this.getSectionsInternal({ galleryId, options }));
  }

  public async getSection(sectionId: string, galleryId: string): Promise<SectionDto> {
    return this.loader.get(`sections_${galleryId}_${sectionId}`, () =>
      this.getSectionInternal({
        sectionId,
        galleryId,
      })
    );
  }

  public async createSection(galleryId: string, type: SectionObjectType, position: number): Promise<SectionDto> {
    let params: any = { gallery: galleryId, object: type, position };
    if (type === 'asset') params = { ...params, ...defaultAssetSection };
    if (type === 'embed') params.embed_width = assetSectionWidths.medium;

    const resp = <SectionDto>await lastValueFrom(this.http.post(`${this.config.apiEndpoint}/sections`, params));
    this.sectionUpdateTimeMap.set(resp.id, Date.now());
    this.loader.removeFuzzy('sections');
    this._updates.next([resp.id, 'created', galleryId]);
    return resp;
  }

  public async getOrCreateSection(
    sectionType: SectionObjectType,
    galleryId: string,
    parentSectionId?: string
  ): Promise<SectionDto> {
    const sections = await this.getSections(galleryId);
    let section = sections.find(s => s.object === sectionType);
    if (parentSectionId) section = sections.find(s => s.id === parentSectionId);
    if (!section) {
      const position = sections.length ? sections.slice().pop().position + 1 : 0;
      section = await this.createSection(galleryId, sectionType, position);
    }
    return <SectionDto>section;
  }

  public async updateSection(sectionId: string, params: Partial<CreateSectionBodyDto>) {
    await lastValueFrom(this.http.patch(`${this.config.apiEndpoint}/sections/${sectionId}`, params));
    this.sectionUpdateTimeMap.set(sectionId, Date.now());
    this.loader.removeFuzzy('sections');
    this.loader.removeFuzzy('galleries'); // clearing last_changed_at
    this._updates.next([sectionId, 'updated']);
  }

  public async deleteSection(section: SectionDto) {
    await lastValueFrom(this.http.delete(`${this.config.apiEndpoint}/sections/${section.id}`));
    this._updates.next([section.id, 'deleted', <string>section.gallery]);
    this.loader.removeFuzzy('sections');
    this.loader.removeFuzzy('assets_');
    this.tenantService.refreshTenant();
  }

  public async fetchAndApplySectionUpdates(sections: SectionDto[], galleryId: string, hack?: boolean): Promise<void> {
    const newSections = await this.getSections(galleryId);
    const toDelete = sections.filter(section => !newSections.find(s => s.id === section.id));
    const toAdd = newSections.filter(section => !sections.find(s => s.id === section.id));
    const toUpdate = sections.filter(section => {
      const newData = newSections.find(s => s.id === section.id);
      if (!newData) return false;
      const lastUpdated = this.sectionUpdateTimeMap.get(section.id) || 0;
      if (lastUpdated - newData.updated_at * 1000 > -1000 && !hack) return false; // if we updated the section within one second of this event then we ignore the update
      return !objectsMatch(section, newData, ['updated_at', 'tenant_product_name']);
    });

    toDelete.forEach(section => {
      const index = sections.indexOf(section);
      sections.splice(index, 1);
    });

    toUpdate.forEach(section => {
      const index = sections.indexOf(section);
      const newData = newSections.find(s => s.id === section.id);
      sections.splice(index, 1, newData);
    });
    sections.push(...toAdd);
  }

  private async getSectionsInternal(data: { galleryId: string; options: { ignore401Errors?: boolean } }) {
    let extra = `?gallery=${data.galleryId}${this.getPassword(data.galleryId)}`;
    if (data.options.ignore401Errors) extra += '&ignore_401_error=true';
    const resp = <any>await lastValueFrom(this.http.get(`${this.config.apiEndpoint}/sections${extra}`));
    const sections: SectionDto[] = resp.data.map(r => this.fixSection(r));
    return sections.filter(s => s.position !== 612512).sort((a, b) => (a.position > b.position ? 1 : -1));
  }

  private async getSectionInternal(data: { sectionId: string; galleryId: string }) {
    const resp = <any>(
      await lastValueFrom(
        this.http.get(`${this.config.apiEndpoint}/sections/${data.sectionId}?${this.getPassword(data.galleryId)}`)
      )
    );
    return resp;
  }

  private fixSection(section: any): SectionDto {
    if (section.spacing === null) section.spacing = 0;
    return section;
  }

  private getPassword(galleryId: string) {
    const password = this.galleryPasswordService.getPassword(galleryId);
    if (!password) return '';
    return '&password=' + encodeURIComponent(password);
  }
}
